<template>
  <div class="px-6 py-6 space-y-6 bg-zinc-950 min-h-screen text-zinc-400 font-light select-none text-left">

    <div class="flex justify-between items-center border-b border-zinc-900/60 pb-5">
      <div class="space-y-1">
        <h1 class="text-xl font-medium text-white tracking-tight">Modifier le Rendez-vous</h1>
        <p class="text-xs text-zinc-500 font-light">Ajustez les détails de la session programmée.</p>
      </div>
      <div class="flex gap-3">
        <button @click="$router.back()" class="px-4 py-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white text-xs font-medium uppercase tracking-wider border border-zinc-800/60 transition-colors cursor-pointer">
          Annuler
        </button>
        <button @click="saveChanges" :disabled="isSubmitting" class="bg-white hover:bg-[#D5CCBC] text-black px-5 py-2.5 rounded-xl text-xs font-semibold uppercase tracking-wider transition-all duration-300 shadow-xl shadow-white/5 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer">
          {{ isSubmitting ? 'Enregistrement...' : 'Enregistrer les modifications' }}
        </button>
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center items-center py-20">
      <div class="text-zinc-500 text-sm">Chargement des données...</div>
    </div>

    <div v-else-if="appointment" class="max-w-3xl space-y-6">

      <!-- Informations client -->
      <div class="bg-zinc-900/20 border border-zinc-900 rounded-2xl p-6 space-y-4">
        <div class="border-b border-zinc-900 pb-3">
          <h2 class="text-sm font-medium text-white tracking-wide">Informations du Client</h2>
        </div>

        <div class="grid md:grid-cols-2 gap-4">
          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium font-mono">Nom complet</label>
            <input v-model="form.nom" type="text" class="w-full px-4 py-2.5 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white focus:outline-none focus:border-zinc-700 text-xs" />
          </div>

          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium font-mono">Téléphone</label>
            <input v-model="form.telephone" type="tel" class="w-full px-4 py-2.5 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white focus:outline-none focus:border-zinc-700 text-xs" />
          </div>

          <div class="space-y-1.5 md:col-span-2">
            <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium font-mono">Email</label>
            <input v-model="form.email" type="email" class="w-full px-4 py-2.5 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white focus:outline-none focus:border-zinc-700 text-xs" />
          </div>
        </div>
      </div>

      <!-- Date et heure -->
      <div class="bg-zinc-900/20 border border-zinc-900 rounded-2xl p-6 space-y-4">
        <div class="border-b border-zinc-900 pb-3">
          <h2 class="text-sm font-medium text-white tracking-wide">Date et Horaires</h2>
        </div>

        <div class="grid md:grid-cols-2 gap-4">
          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium font-mono">Date</label>
            <input v-model="form.date" type="date" class="w-full px-4 py-2.5 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white focus:outline-none focus:border-zinc-700 text-xs" />
          </div>

          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium font-mono">Heure de début</label>
            <input v-model="form.heureDebut" type="time" class="w-full px-4 py-2.5 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white focus:outline-none focus:border-zinc-700 text-xs" />
          </div>

          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium font-mono">Heure de fin</label>
            <input v-model="form.heureFin" type="time" class="w-full px-4 py-2.5 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white focus:outline-none focus:border-zinc-700 text-xs" />
          </div>

          <div class="space-y-1.5">
            <label class="block text-[10px] uppercase tracking-widest text-zinc-500 font-medium font-mono">Statut</label>
            <select v-model="form.status" class="w-full px-4 py-2.5 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white focus:outline-none focus:border-zinc-700 text-xs cursor-pointer">
              <option value="En attente">En attente</option>
              <option value="Validé">Validé</option>
              <option value="Annulé">Annulé</option>
              <option value="Terminé">Terminé</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Prestations -->
      <div class="bg-zinc-900/20 border border-zinc-900 rounded-2xl p-6 space-y-4">
        <div class="border-b border-zinc-900 pb-3 flex items-center justify-between">
          <h2 class="text-sm font-medium text-white tracking-wide">Prestations</h2>
          <button
            @click="showServiceSelector = true"
            type="button"
            class="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white rounded-lg text-[10px] font-medium uppercase tracking-wider border border-zinc-800 transition-colors cursor-pointer"
          >
            + Ajouter
          </button>
        </div>

        <div v-if="form.services.length === 0" class="text-center py-8 text-zinc-600 text-xs">
          Aucune prestation sélectionnée
        </div>

        <div v-else class="space-y-2">
          <div
            v-for="(service, index) in form.services"
            :key="service._id || index"
            class="p-3 bg-zinc-900/40 border border-zinc-800 rounded-xl flex items-center justify-between hover:border-zinc-700 transition-colors"
          >
            <div>
              <div class="text-xs text-white font-medium">{{ service.nom || 'Service sans nom' }}</div>
              <div class="text-[10px] text-zinc-500">
                {{ service.prix || 0 }} € • {{ service.duree || 0 }} min
              </div>
            </div>
            <button
              @click="removeService(index)"
              type="button"
              class="text-red-400 hover:text-red-300 text-xs px-2 py-1 rounded transition-colors"
              title="Supprimer cette prestation"
            >
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>

        <div class="pt-3 border-t border-zinc-900">
          <div class="flex justify-between items-center">
            <span class="text-xs font-medium text-zinc-400">Total des prestations</span>
            <span class="text-lg font-semibold text-[#D5CCBC]">{{ calculateTotal() }} €</span>
          </div>
        </div>
      </div>

      <!-- Notes -->
      <div class="bg-zinc-900/20 border border-zinc-900 rounded-2xl p-6 space-y-4">
        <div class="border-b border-zinc-900 pb-3">
          <h2 class="text-sm font-medium text-white tracking-wide">Notes</h2>
        </div>

        <div class="space-y-1.5">
          <textarea
            v-model="form.notes"
            rows="4"
            placeholder="Ajouter des notes ou instructions spécifiques..."
            class="w-full px-4 py-2.5 rounded-xl border border-zinc-900 bg-zinc-900/40 text-white placeholder-zinc-700 focus:outline-none focus:border-zinc-700 text-xs resize-none"
          ></textarea>
        </div>
      </div>

    </div>

    <!-- Modal de sélection de services -->
    <div
      v-if="showServiceSelector"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      @click.self="showServiceSelector = false"
    >
      <div class="bg-zinc-900 rounded-2xl border border-zinc-800 p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div class="flex items-center justify-between mb-5 border-b border-zinc-800 pb-4">
          <h3 class="text-sm font-medium text-white">Sélectionner des prestations</h3>
          <button @click="showServiceSelector = false" class="text-zinc-500 hover:text-white transition-colors">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div v-if="loadingServices" class="flex justify-center py-8">
          <div class="text-zinc-500 text-xs">Chargement des services...</div>
        </div>

        <div v-else class="space-y-2">
          <div
            v-for="service in availableServices"
            :key="service._id"
            @click="addService(service)"
            class="p-4 bg-zinc-900/40 border border-zinc-800 rounded-xl hover:border-zinc-700 hover:bg-zinc-900/60 cursor-pointer transition-all"
            :class="{ 'opacity-50 cursor-not-allowed': isServiceSelected(service._id) }"
          >
            <div class="flex items-center justify-between">
              <div>
                <div class="text-xs text-white font-medium">{{ service.nom }}</div>
                <div class="text-[10px] text-zinc-500 mt-1">
                  {{ service.prix }} € • {{ service.duree }} min
                  <span v-if="service.categorie?.nom" class="ml-2 text-zinc-600">• {{ service.categorie.nom }}</span>
                </div>
              </div>
              <div v-if="isServiceSelected(service._id)" class="text-emerald-500 text-xs">
                <i class="fas fa-check"></i> Sélectionné
              </div>
            </div>
          </div>

          <div v-if="availableServices.length === 0" class="text-center py-8 text-zinc-600 text-xs">
            Aucun service disponible
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import api from '@/api'

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)
const isSubmitting = ref(false)
const appointment = ref(null)
const showServiceSelector = ref(false)
const availableServices = ref([])
const loadingServices = ref(false)

const form = reactive({
  nom: '',
  telephone: '',
  email: '',
  date: '',
  heureDebut: '',
  heureFin: '',
  status: 'En attente',
  services: [],
  notes: ''
})

// 🔧 Watcher : Recalculer l'heure de fin quand l'heure de début change
watch(() => form.heureDebut, (newValue) => {
  // Recalculer seulement si on a une heure de début ET des services
  if (newValue && form.services.length > 0 && !isLoading.value) {
    recalculateEndTime()
  }
})

// Charger tous les services disponibles
const loadServices = async () => {
  try {
    loadingServices.value = true
    const response = await api.services.getAllServices()
    availableServices.value = response.data || []
    console.log('✅ Services chargés:', availableServices.value.length)
  } catch (error) {
    console.error('Erreur lors du chargement des services:', error)
    toast.error('Erreur lors du chargement des services')
  } finally {
    loadingServices.value = false
  }
}

// Charger les données du rendez-vous
const loadAppointment = async () => {
  try {
    isLoading.value = true
    const appointmentId = route.params.id

    const response = await api.appointments.getAppointmentById(appointmentId)
    appointment.value = response.data

    console.log('📋 Données du rendez-vous reçues:', appointment.value)
    console.log('👤 Client:', appointment.value.client)

    // Remplir le formulaire avec les informations du client
    form.nom = appointment.value.nom || ''
    form.telephone = appointment.value.telephone || ''
    form.email = appointment.value.email || ''
    form.notes = appointment.value.notes || ''

    console.log('✍️ Formulaire rempli:', {
      nom: form.nom,
      telephone: form.telephone,
      email: form.email
    })

    // Gérer à la fois 'service' et 'services' pour la compatibilité
    form.services = appointment.value.service || appointment.value.services || []
    console.log('🔧 Services mappés:', form.services)

    // Mapper le statut backend vers frontend
    const statusMap = {
      'confirmé': 'Validé',
      'en_attente': 'En attente',
      'annulé': 'Annulé',
      'terminer': 'Terminé'
    }

    // L'API retourne déjà le statut formaté dans 'status' (ex: "Validé")
    // Sinon, on essaie de mapper depuis 'statut' (ex: "confirmé")
    if (appointment.value.status && ['En attente', 'Validé', 'Annulé', 'Terminé'].includes(appointment.value.status)) {
      form.status = appointment.value.status
    } else {
      form.status = statusMap[appointment.value.statut] || 'En attente'
    }

    // Gérer les dates
    if (appointment.value.start) {
      const startDate = new Date(appointment.value.start)
      form.date = startDate.toISOString().split('T')[0]
      form.heureDebut = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`
    } else if (appointment.value.date_rdv) {
      const startDate = new Date(appointment.value.date_rdv)
      form.date = startDate.toISOString().split('T')[0]
      form.heureDebut = `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`
    }

    if (appointment.value.end) {
      const endDate = new Date(appointment.value.end)
      form.heureFin = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`
    } else if (appointment.value.duree_minutes && form.heureDebut) {
      // Calculer l'heure de fin basée sur la durée
      const [hours, minutes] = form.heureDebut.split(':').map(Number)
      const totalMinutes = hours * 60 + minutes + appointment.value.duree_minutes
      const endHours = Math.floor(totalMinutes / 60)
      const endMinutes = totalMinutes % 60
      form.heureFin = `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`
    }

    // 🔧 Recalculer l'heure de fin basée sur les services pour garantir la cohérence
    // Ceci écrasera l'heure de fin calculée ci-dessus avec la durée réelle des services
    if (form.services.length > 0 && form.heureDebut) {
      recalculateEndTime()
    }

  } catch (error) {
    console.error('Erreur lors du chargement du rendez-vous:', error)
    toast.error('Erreur lors du chargement du rendez-vous')
    router.back()
  } finally {
    isLoading.value = false
  }
}

// Recalculer l'heure de fin basée sur les services sélectionnés
const recalculateEndTime = () => {
  if (!form.heureDebut) return

  // Calculer la durée totale de tous les services
  const totalDuration = form.services.reduce((sum, service) => sum + (service.duree || 0), 0)

  // Convertir l'heure de début en minutes
  const [hours, minutes] = form.heureDebut.split(':').map(Number)
  const startMinutes = hours * 60 + minutes

  // Ajouter la durée totale
  const endMinutes = startMinutes + totalDuration

  // Convertir en heures et minutes
  const endHours = Math.floor(endMinutes / 60)
  const endMins = endMinutes % 60

  // Mettre à jour l'heure de fin
  form.heureFin = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`

  console.log(`🕒 Durée recalculée : ${totalDuration} minutes (${form.heureDebut} → ${form.heureFin})`)
}

// Ajouter un service
const addService = (service) => {
  if (!isServiceSelected(service._id)) {
    form.services.push({
      _id: service._id,
      nom: service.nom,
      prix: service.prix,
      duree: service.duree
    })
    toast.success(`Service "${service.nom}" ajouté`)

    // 🔧 Recalculer automatiquement l'heure de fin
    recalculateEndTime()
  }
}

// Supprimer un service
const removeService = (index) => {
  const serviceName = form.services[index].nom
  form.services.splice(index, 1)
  toast.info(`Service "${serviceName}" retiré`)

  // 🔧 Recalculer automatiquement l'heure de fin
  recalculateEndTime()
}

// Vérifier si un service est déjà sélectionné
const isServiceSelected = (serviceId) => {
  return form.services.some(s => s._id === serviceId)
}

// Calculer le total
const calculateTotal = () => {
  return form.services.reduce((sum, service) => sum + (service.prix || 0), 0).toFixed(2)
}

// Sauvegarder les modifications
const saveChanges = async () => {
  try {
    isSubmitting.value = true

    // Validation
    if (!form.nom || !form.date || !form.heureDebut || !form.heureFin) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      isSubmitting.value = false
      return
    }

    if (form.services.length === 0) {
      toast.error('Veuillez sélectionner au moins une prestation')
      isSubmitting.value = false
      return
    }

    // Créer les objets Date pour start et end
    const startDateTime = new Date(`${form.date}T${form.heureDebut}:00`)
    const endDateTime = new Date(`${form.date}T${form.heureFin}:00`)

    // Extraire seulement les IDs des services
    const serviceIds = form.services.map(s => s._id)

    // Préserver l'ID de l'employé depuis le RDV original
    const employeId = appointment.value.employe?.id || appointment.value.employe?._id || appointment.value.employe

    const payload = {
      nom: form.nom,
      telephone: form.telephone,
      email: form.email,
      start: startDateTime.toISOString(),
      end: endDateTime.toISOString(),
      status: form.status,
      notes: form.notes,
      service: serviceIds // Envoyer un tableau d'IDs
    }

    // 🔧 Ajouter l'employé seulement s'il existe (pour le préserver)
    if (employeId) {
      payload.employe = employeId
    }

    console.log('💾 Payload envoyé:', payload)
    console.log('📋 Rendez-vous original:', appointment.value)
    console.log('👤 Employé original:', appointment.value.employe)

    await api.appointments.updateAppointment(route.params.id, payload)

    toast.success('Rendez-vous modifié avec succès')
    router.push('/appointments')

  } catch (error) {
    console.error('Erreur lors de la modification:', error)
    toast.error(error.response?.data?.message || 'Erreur lors de la modification du rendez-vous')
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  loadAppointment()
  loadServices()
})
</script>
